/**
 * Phone Login Manager for authentication
 * Handles phone-based login flow with reCAPTCHA for LogInScreen
 */

import React, { useState, useEffect, useRef } from 'react';
import { View, Platform, Alert } from 'react-native';
import { getAuth, signInWithPhoneNumber, ConfirmationResult } from 'firebase/auth';
import PhoneVerificationModal from '../components/PhoneVerificationModal';
import { formatPhoneNumberE164 } from '../utils/phoneFormatting';
import { validatePhoneNumber } from '../utils/phoneValidation';
import { initializeRecaptcha, cleanupRecaptcha, renderRecaptcha } from '../utils/recaptchaSetup';
import { getAuthErrorMessage } from '../utils/smsErrors';
import DataOps from '../../data/DataOps';

interface PhoneLoginManagerProps {
  /** Firebase app instance */
  firebaseApp: any;
  /** Whether the login screen is visible */
  visible: boolean;
  /** Callback when verification is complete */
  onVerificationComplete: (userData: { phoneNumber: string; firebaseUid: string } | null, existingUser?: any) => void;
  /** Optional container ID for reCAPTCHA (defaults to 'recaptcha-container-login') */
  recaptchaContainerId?: string;
}

/**
 * Hook to manage phone login state and flow
 */
export const usePhoneLogin = ({
  firebaseApp,
  visible,
  onVerificationComplete,
  recaptchaContainerId = 'recaptcha-container-login',
}: PhoneLoginManagerProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [pendingPhoneE164, setPendingPhoneE164] = useState('');
  const confirmationResultRef = useRef<ConfirmationResult | null>(null);
  const recaptchaRef = useRef<any>(null);
  const pendingPhoneRef = useRef<string>('');

  // Initialize invisible reCAPTCHA (web) once the screen becomes visible
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (!visible) return;

    try {
      if (!recaptchaRef.current) {
        recaptchaRef.current = initializeRecaptcha(firebaseApp, {
          containerId: recaptchaContainerId,
          size: 'invisible',
          callback: async () => {
            await sendSMS();
          },
        });
        
        setTimeout(() => {
          try {
            renderRecaptcha(recaptchaRef.current);
          } catch {}
        }, 0);
      }
    } catch (e: any) {
      // Silent fail - reCAPTCHA initialization will retry
    }
  }, [visible, firebaseApp, recaptchaContainerId]);

  /**
   * Sends SMS verification code
   */
  const sendSMS = async () => {
    const phone = pendingPhoneRef.current || pendingPhoneE164 || '+1';
    if (!phone || phone === '+1') throw new Error('Empty or invalid formatted phone.');

    // Open modal immediately
    setShowVerificationModal(true);
    setIsLoading(true);

    try {
      const auth = getAuth(firebaseApp);

      const confirmation = await signInWithPhoneNumber(
        auth,
        phone,
        Platform.OS === 'web' ? recaptchaRef.current : undefined
      );

      confirmationResultRef.current = confirmation;
      Alert.alert('Success', `Verification code sent to ${phone}`);
    } catch (error: any) {
      const msg = getAuthErrorMessage(error?.code, `Failed to send verification code: ${error?.message || 'Unknown error'}`);
      Alert.alert('Error', msg);
      setShowVerificationModal(false);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handles the continue button from the phone input screen
   */
  const handleContinue = async (phoneRaw: string) => {
    const validation = validatePhoneNumber(phoneRaw);
    if (!validation.ok) {
      setPhoneError(validation.msg);
      return;
    }
    setPhoneError('');

    const e164 = formatPhoneNumberE164(phoneRaw);
    setPendingPhoneE164(e164);
    pendingPhoneRef.current = e164; // Store in ref for reCAPTCHA callback

    // Web: trigger invisible reCAPTCHA; Mobile: send SMS directly
    if (Platform.OS === 'web') {
      if (!recaptchaRef.current) {
        Alert.alert('Error', 'reCAPTCHA not ready. Please try again.');
        return;
      }
      try {
        setIsLoading(true);
        await recaptchaRef.current.verify();
      } catch (e) {
        Alert.alert('Error', 'Please complete reCAPTCHA and try again.');
        setIsLoading(false);
      }
      return;
    }

    await sendSMS();
  };

  /**
   * Verifies the code entered by the user
   */
  const handleVerify = async () => {
    if (!verificationCode.trim()) {
      Alert.alert('Error', 'Please enter the verification code');
      return;
    }
    if (!confirmationResultRef.current) {
      Alert.alert('Error', 'No verification session found');
      return;
    }

    setIsLoading(true);
    try {
      const result = await confirmationResultRef.current.confirm(verificationCode);
      const firebaseUser = result.user;
      const phoneNumber = firebaseUser.phoneNumber || pendingPhoneE164;

      // Check if user already exists in any collection
      const userResult = await DataOps.fetchUserFromAnyCollection(firebaseUser.uid);

      if (userResult) {
        // Existing user - update phone verification status if needed
        const { user: existingUser, collection } = userResult;
        
        const needsUpdate = !existingUser.phoneVerified || !existingUser.dateJoined;
        if (needsUpdate) {
          const updatedUser = {
            ...existingUser,
            phoneVerified: true,
            primaryContactMethod: 'phone',
            dateJoined: existingUser.dateJoined || new Date().toISOString()
          };
          
          await DataOps.saveCustomDataToFirebase(collection, existingUser.userId, updatedUser, true);
          await DataOps.saveDataToStorage('user', updatedUser);
          
          Alert.alert('Success', 'Welcome back!');
          onVerificationComplete(null, updatedUser);
        } else {
          await DataOps.saveDataToStorage('user', existingUser);
          
          Alert.alert('Success', 'Welcome back!');
          onVerificationComplete(null, existingUser);
        }
      } else {
        // New user - return data for account setup
        onVerificationComplete({
          phoneNumber: phoneNumber,
          firebaseUid: firebaseUser.uid,
        });
      }

      setShowVerificationModal(false);
      setVerificationCode('');
      confirmationResultRef.current = null;
    } catch (error: any) {
      const msg = getAuthErrorMessage(error?.code, 'Invalid verification code');
      Alert.alert('Error', msg);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Resends the verification code
   */
  const handleResend = async () => {
    if (Platform.OS === 'web' && recaptchaRef.current) {
      try {
        await recaptchaRef.current.verify();
      } catch {}
      return;
    }
    await sendSMS();
  };

  /**
   * Closes the verification modal
   */
  const handleCloseVerification = () => {
    setShowVerificationModal(false);
    setVerificationCode('');
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupRecaptcha(recaptchaRef.current);
    };
  }, []);

  return {
    isLoading,
    phoneError,
    showVerificationModal,
    verificationCode,
    setVerificationCode,
    pendingPhoneE164,
    confirmationResultRef: confirmationResultRef.current,
    handleContinue,
    handleVerify,
    handleResend,
    handleCloseVerification,
  };
};

/**
 * Phone Login Manager Component
 * Renders the reCAPTCHA container and verification modal
 */
const PhoneLoginManager: React.FC<PhoneLoginManagerProps & { children?: React.ReactNode }> = ({
  firebaseApp,
  visible,
  onVerificationComplete,
  recaptchaContainerId = 'recaptcha-container-login',
  children,
}) => {
  const phoneLogin = usePhoneLogin({
    firebaseApp,
    visible,
    onVerificationComplete,
    recaptchaContainerId,
  });

  return (
    <>
      {Platform.OS === 'web' && (
        <View style={{ height: 0, width: 0, overflow: 'hidden' }}>
          <View nativeID={recaptchaContainerId} />
        </View>
      )}

      {children}

      {/* Phone verification modal */}
      <PhoneVerificationModal
        visible={visible && phoneLogin.showVerificationModal}
        onClose={phoneLogin.handleCloseVerification}
        onVerify={phoneLogin.handleVerify}
        onResend={phoneLogin.handleResend}
        isLoading={phoneLogin.isLoading}
        verificationCode={phoneLogin.verificationCode}
        setVerificationCode={phoneLogin.setVerificationCode}
        phoneE164={phoneLogin.pendingPhoneE164}
      />
    </>
  );
};

export default PhoneLoginManager;

